import { HTTP_CONSTANTS } from '@dch/entity-manager';
import { Injectable } from '@angular/core';
import { RemoteLoggerService } from '../services/remote-logger.service';
import {
  IDeferralRequiredInfo,
  SelectedOdbDetailsInfo,
} from '../models/pii.model';
import {
  IBillingBalance,
  IBillingDocument,
  IBillingTransactions,
  IOdbBillCategoryDetailSection,
  IOdbBillProductSplit,
} from '@dch/billing-business-logic';
import { differenceInCalendarMonths } from 'date-fns';
import { BillExplanationType } from '../models/extra-cost.model';
import {
  IBillingDocumentReference,
  IBillingDocumentReferences,
} from '../models/billing-transactions-references.model';
import { TranslationService } from '@dch/ng-common';

export const SUMMARY_DETAILS_SESSION_DATA_KEY = 'summaryPageParamsDataKey';
export const ODB_DETAILS_SESSION_DATA_KEY =
  'odbCategoryDetailsPageParamsDataKey';
export const ODB_THIRD_PARTY_L3_PAGE_SESSION_DATA_KEY =
  'odbThirdPartyL3PageParamsDataKey';
export const ODB_THIRD_PARTY_L4_PAGE_SESSION_DATA_KEY =
  'odbThirdPartyL4PageParamsDataKey';

const MAGNOLIA_SEGMENTS = {
  RES: 'Personal',
  SE: 'Self-employed-and-small-companies',
  PRE: 'Personal',
  COR: 'companies-and-government',
  '': 'Guest',
};

@Injectable()
export class UtilsService {
  constructor(
    private remoteLoggerService: RemoteLoggerService,
    private translationService: TranslationService
  ) {}

  setValueInSessionStorage<T>(key: string, value: T) {
    sessionStorage.setItem(key, JSON.stringify(value));
  }

  getInfoFromSessionStorage<T>(key: string): T | undefined {
    let parsedData: T | undefined;
    const data = sessionStorage.getItem(key);
    if (data) {
      try {
        parsedData = JSON.parse(data);
      } catch (error) {
        this.remoteLoggerService.logError(
          `Failed parsing JSON for key ${key} with error: ${error}`
        );
      }
    }
    return parsedData;
  }

  getDeferralDocumentInfoFromStorage(): IDeferralRequiredInfo | undefined {
    const selectedOdbDocumentInfo =
      this.getInfoFromSessionStorage<SelectedOdbDetailsInfo>(
        ODB_DETAILS_SESSION_DATA_KEY
      );
    return {
      billDocument: selectedOdbDocumentInfo?.billDocument,
      billingAccountNumber:
        selectedOdbDocumentInfo?.billingAccount?.accountNumber,
      customerPaymentProfile: selectedOdbDocumentInfo?.customerPaymentProfile,
    };
  }

  isPeppolUBLXmlApiAvailable() {
    return (
      this.translationService.getTranslatedValueFor(
        'bills.cover.isUBLXmlApiActive'
      ) === 'true'
    );
  }
}

export function getFormattedURLFor(
  translatedValue: string,
  segment: 'RES' | 'SE' | 'PRE' | 'COR' | ''
): string {
  return translatedValue.replace(/{{segment}}/g, MAGNOLIA_SEGMENTS[segment]);
}

export function getSmoothAccessRequiredHeaders(): string[] {
  return window.location.pathname.indexOf('/smoothAccess') === 0
    ? [HTTP_CONSTANTS.X_APPLICATION_AUTHORIZATION_HEADER]
    : [];
}

export function getDeferralCardVariant(
  customerPaymentProfile: IBillingBalance['customerPaymentProfile'],
  partialDeferralCheck: IBillingDocument['partialDeferralCheck']
): '1' | '2' | '3' | '4' {
  if (['3', '5', '6', '7'].includes(customerPaymentProfile)) {
    if (partialDeferralCheck.showDeferral === true) {
      return '1';
    } else if (
      ['3', '4', '5', '6', '8'].includes(partialDeferralCheck.reason)
    ) {
      return '3';
    } else if ('2' === partialDeferralCheck.reason) {
      return '2';
    }
  }
  return '4';
}

export function isDocumentLesserThan12MonthsOld(documentDate: string) {
  const [day, month, year] = documentDate.split('/');
  return (
    differenceInCalendarMonths(
      Date.now(),
      new Date(Number(year), Number(month) - 1, Number(day))
    ) <= 12
  );
}

export function getBillExplanationType(
  billingTransaction: IBillingTransactions,
  billDocument: IBillingDocument
): BillExplanationType {
  const isDocumentHasPositiveAmount = Number(billDocument.amount) >= 0;
  const hasClosedTransactions =
    billingTransaction.recentClosedTransactions.length > 0;

  if (
    hasClosedTransactions &&
    findDocumentIn(
      billingTransaction.recentClosedTransactions,
      billDocument.reference
    )
  ) {
    return isDocumentHasPositiveAmount ? 'billComposition' : 'creditNote';
  }

  const isDocumentFoundInOpenTransactions = findDocumentIn(
    billingTransaction.openTransactions.billingDocuments,
    billDocument.reference
  );

  if (
    hasClosedTransactions &&
    findDocumentIn(
      billingTransaction.openTransactions.billingDocuments,
      billDocument.reference
    )
  ) {
    return isDocumentHasPositiveAmount ? 'billComposition' : 'creditNote';
  } else if (isDocumentFoundInOpenTransactions) {
    return isDocumentHasPositiveAmount ? 'firstInvoice' : 'creditNote';
  }
}

export function getProductIdsFromProductSplit(
  filteredProductSplit: Array<IOdbBillProductSplit>
): string[] {
  if (filteredProductSplit?.length > 0) {
    const combinedCategories = filteredProductSplit.reduce(
      (c, v) => c.concat(v.categoryDetails),
      []
    );
    const combinedSections = combinedCategories
      .reduce((x, y) => x.concat(y.sections), [])
      .filter((sections) => sections?.items?.length > 0);

    return getProductIdsFromSections(combinedSections);
  } else {
    return [];
  }
}

export function getProductIdsFromSections(
  sections: Array<IOdbBillCategoryDetailSection>
) {
  const combinedItems = sections
    .reduce((x, y) => x.concat(y.items), [])
    .map((item) => {
      if (
        item.hasOwnProperty('productId') &&
        item?.productId !== undefined &&
        Number(item.amount) > 0
      ) {
        return item.productId;
      }
    })
    .filter((item) => item !== undefined);
  return combinedItems.length > 0 ? Array.from(new Set(combinedItems)) : [];
}

export function getThirdPartyDocumentReference(
  billingDocumentReferences: IBillingDocumentReferences[],
  orderId: string
): IBillingDocumentReference {
  return getDocumentReferencesForOrderId(
    billingDocumentReferences,
    orderId
  )?.documents.find((document) => document.type === 'MC');
}

export function getThirdPartyTransactionReferenceForOrderId(
  billingDocumentReferences: IBillingDocumentReference[]
): string | undefined {
  return billingDocumentReferences.find((document) => document.type === 'MC')
    ?.reference;
}

export function getDocumentReferencesForOrderId(
  billingDocumentReferences: IBillingDocumentReferences[],
  orderId: string
) {
  return billingDocumentReferences.find(
    (billDocument) => billDocument.orderId === orderId
  );
}

function findDocumentIn(
  billingDocuments: IBillingDocument[],
  documentReference: string
) {
  return billingDocuments.some(
    (billDocument) => billDocument.reference === documentReference
  );
}
